import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Animated, Linking } from 'react-native';
import { Typography, Box, Button } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';

interface ClaimStoreScreenProps {
    subdomain?: string;
    primaryColor?: string;
}

export const ClaimStoreScreen: React.FC<ClaimStoreScreenProps> = ({ 
    subdomain = 'this-store',
    primaryColor = '#3b82f6'
}) => {
    const [fadeAnim] = useState(new Animated.Value(0));
    const [floatAnim] = useState(new Animated.Value(0));

    // Darken color for background (make it 70% darker)
    const darkenColor = (color: string, percent: number) => {
        const num = parseInt(color.replace('#', ''), 16);
        const amt = Math.round(2.55 * percent);
        const R = Math.max(0, (num >> 16) - amt);
        const G = Math.max(0, (num >> 8 & 0x00FF) - amt);
        const B = Math.max(0, (num & 0x0000FF) - amt);
        return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
    };

    const darkBg = darkenColor(primaryColor, 35);
    const mediumBg = darkenColor(primaryColor, 85);

    useEffect(() => {
        // Fade in animation
        Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: false,
        }).start();

        // Floating animation for icon
        Animated.loop(
            Animated.sequence([
                Animated.timing(floatAnim, {
                    toValue: 1,
                    duration: 2000,
                    useNativeDriver: false,
                }),
                Animated.timing(floatAnim, {
                    toValue: 0,
                    duration: 2000,
                    useNativeDriver: false,
                })
            ])
        ).start();
    }, []);

    const iconFloat = floatAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [0, -15],
    });

    const handleClaimStore = () => {
        Linking.openURL('https://cosmocart.shop').catch(err => {
            console.error('Failed to open URL:', err);
            // Fallback to window.open for web
            if (typeof window !== 'undefined') {
                window.location.href = 'https://cosmocart.shop';
            }
        });
    };

    return (
        <View style={[styles.container, { backgroundColor: darkBg }]}>
            {/* Background with enhanced grid pattern */}
            <Box sx={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: mediumBg,
            }}>
                {/* Primary grid - large pattern with offset */}
                <Box sx={{ 
                    position: 'absolute',
                    top: '-10%',
                    left: '-5%',
                    right: '-5%',
                    bottom: '-10%',
                    backgroundImage: `
                        linear-gradient(${primaryColor}18 2px, transparent 2px),
                        linear-gradient(90deg, ${primaryColor}18 2px, transparent 2px)
                    `,
                    backgroundSize: '100px 100px',
                    backgroundPosition: '0 -100px',
                    opacity: 0.5,
                    transform: 'rotate(-2deg)',
                }} />
                
                {/* Secondary grid - smaller, diagonal offset */}
                <Box sx={{ 
                    position: 'absolute',
                    top: '-10%',
                    left: '-5%',
                    right: '-5%',
                    bottom: '-10%',
                    backgroundImage: `
                        linear-gradient(${primaryColor}10 1px, transparent 1px),
                        linear-gradient(90deg, ${primaryColor}10 1px, transparent 1px)
                    `,
                    backgroundSize: '50px 50px',
                    backgroundPosition: '25px -75px',
                    opacity: 0.4,
                    transform: 'rotate(1deg)',
                }} />

                {/* Radial vignette overlay for depth */}
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `radial-gradient(ellipse at 50% 35%, transparent 0%, ${darkBg}70 65%, ${darkBg} 100%)`,
                }} />
            </Box>

            <Animated.View style={[styles.content, { opacity: fadeAnim }]}>
                {/* Subtle glow overlay using primary color */}
                <Box sx={[styles.gradientOverlay, { 
                    background: `radial-gradient(circle at 50% 25%, ${primaryColor}12 0%, transparent 60%)`,
                }]} />
                
                {/* Floating Icon with Glow */}
                <Animated.View 
                    style={[
                        styles.iconWrapper,
                        { transform: [{ translateY: iconFloat }] }
                    ]}
                >
                    <Box
                        sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.08)',
                            borderRadius: '50%',
                            padding: { xs: 2, sm: 3, md: 4 },
                            border: `2px solid ${primaryColor}40`,
                            boxShadow: `
                                0 0 60px ${primaryColor}50,
                                0 0 120px ${primaryColor}30,
                                inset 0 0 30px ${primaryColor}20
                            `,
                            marginBottom: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Box
                            component="img"
                            src={require('../assets/cosmo-logo.png')}
                            alt="Cosmo Cart Logo"
                            sx={{ 
                                width: { xs: 60, sm: 70, md: 80 },
                                height: { xs: 60, sm: 70, md: 80 },
                                objectFit: 'contain',
                                filter: `drop-shadow(0 0 20px ${primaryColor}70)`,
                            }}
                        />
                    </Box>
                </Animated.View>
                
                {/* Subdomain Display */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        marginBottom: 2,
                        padding: '6px 20px',
                        backgroundColor: `${primaryColor}15`,
                        borderRadius: '20px',
                        border: `1px solid ${primaryColor}30`,
                    }}
                >
                    <Typography 
                        variant='body2' 
                        sx={{ 
                            color: 'rgba(255, 255, 255, 0.5)',
                            fontWeight: 500,
                            fontSize: { xs: '0.7rem', sm: '0.85rem', md: '0.95rem' },
                        }}
                    >
                        {subdomain}.cosmocart.shop
                    </Typography>
                </Box>

                {/* Store Available Badge */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        marginY: 3,
                        padding: '12px 32px',
                        backgroundColor: `${primaryColor}20`,
                        borderRadius: '30px',
                        border: `2px solid ${primaryColor}40`,
                    }}
                >
                    <Typography 
                        variant='h6' 
                        sx={{ 
                            color: primaryColor,
                            fontWeight: 700,
                            letterSpacing: '1px',
                            fontSize: { xs: '0.85rem', sm: '1rem', md: '1.15rem' },
                        }}
                    >
                        Store Available
                    </Typography>
                </Box>

                {/* Message */}
                <Typography 
                    sx={{ 
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: { xs: '0.85rem', sm: '1rem', md: '1.15rem' },
                        
                        textAlign: 'center',
                        maxWidth: 600,
                        marginBottom: 4,
                        paddingX: 2,
                    }}
                >
                    This store is not yet claimed. <br /> Start your e-commerce journey today with Cosmo Cart!
                </Typography>

                {/* CTA Button */}
                <Button
                    variant="contained"
                    size="medium"
                    endIcon={<ArrowForward />}
                    onClick={handleClaimStore}
                    sx={{
                        marginTop: 2,
                        backgroundColor: primaryColor,
                        color: 'white',
                        paddingX: { xs: 3, sm: 4, md: 5 },
                        paddingY: { xs: 1.5, sm: 1.75, md: 2 },
                        fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' },
                        fontWeight: 700,
                        borderRadius: '50px',
                        textTransform: 'none',
                        boxShadow: `
                            0 8px 32px ${primaryColor}60,
                            0 0 60px ${primaryColor}30
                        `,
                        border: `2px solid ${primaryColor}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            backgroundColor: primaryColor,
                            filter: 'brightness(1.2)',
                            boxShadow: `
                                0 12px 48px ${primaryColor}80,
                                0 0 80px ${primaryColor}50
                            `,
                            transform: 'translateY(-2px)',
                        },
                        '&:active': {
                            transform: 'translateY(0px)',
                        }
                    }}
                >
                    Claim This Store
                </Button>

                {/* Additional Info */}
                <Typography 
                    sx={{ 
                        color: 'rgba(255, 255, 255, 0.4)',
                        fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' },
                        marginTop: 3,
                        textAlign: 'center',
                    }}
                >
                    Powered by Cosmo Cart © {new Date().getFullYear()}
                </Typography>
            </Animated.View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        zIndex: 9999,
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        backgroundColor: '#0f172a', // Fallback color
    },
    content: {
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        paddingTop: 24,
        paddingBottom: 24,
        maxWidth: 700,
        width: '100%',
        zIndex: 2,
    },
    iconWrapper: {
        marginBottom: 8,
        zIndex: 2,
    },
    gradientOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
        pointerEvents: 'none',
    },
});

